import { useEffect, useRef, useSyncExternalStore } from 'react';

import { focusManager } from '@tanstack/react-query';

import { useAccessToken } from '@/services/auth';

const RETRY_DELAYS_MS = [1000, 2000, 5000, 15000];
const MAX_FIRST_RETRY_JITTER_MS = 3000;

function toEventSourceUrl(path: string, token: string) {
  const url = new URL(
    `${import.meta.env.VITE_API_BASE_URL}${path}`,
    window.location.href,
  );

  url.searchParams.set('token', token);

  return url.toString();
}

export type EventSourceMeta = Record<string, unknown>;

export type EventSourceContext<TMeta extends EventSourceMeta> = {
  meta: TMeta;
};

export type UseEventSourceOptions<
  TReceive,
  TMeta extends EventSourceMeta = EventSourceMeta,
> = {
  path: string;
  meta?: TMeta;
  onMessage: (data: TReceive, context: EventSourceContext<TMeta>) => void;
  onReconnect?: (context: EventSourceContext<TMeta>) => void;
  enabled?: boolean;
  retryDelay?: (attempt: number) => number;
};

export function defaultRetryDelay(attempt: number) {
  const delay = RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)];

  return attempt === 0
    ? delay + Math.random() * MAX_FIRST_RETRY_JITTER_MS
    : delay;
}

const subscribeToFocus = (onChange: () => void) =>
  focusManager.subscribe(onChange);

const isFocused = () => focusManager.isFocused();

function useIsFocused() {
  return useSyncExternalStore(subscribeToFocus, isFocused, () => true);
}

export function useEventSource<
  TReceive,
  TMeta extends EventSourceMeta = EventSourceMeta,
>(options: UseEventSourceOptions<TReceive, TMeta>) {
  const { path, enabled = true } = options;
  const token = useAccessToken();
  const focused = useIsFocused();
  const optionsRef = useRef(options);
  const openedPathRef = useRef<string>();

  useEffect(() => {
    optionsRef.current = options;
  });

  const active = enabled && !!token && focused;

  useEffect(() => {
    if (!active) {
      return;
    }

    let source: EventSource | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let attempt = 0;

    const context = (): EventSourceContext<TMeta> => ({
      meta: (optionsRef.current.meta ?? {}) as TMeta,
    });

    const open = () => {
      const current = new EventSource(toEventSourceUrl(path, token));
      source = current;

      current.onopen = () => {
        attempt = 0;

        if (openedPathRef.current === path) {
          optionsRef.current.onReconnect?.(context());
        }

        openedPathRef.current = path;
      };

      current.onmessage = (event: MessageEvent<string>) => {
        let data: TReceive;

        try {
          data = JSON.parse(event.data);
        } catch {
          return;
        }

        optionsRef.current.onMessage(data, context());
      };

      current.onerror = () => {
        if (current.readyState !== EventSource.CLOSED) {
          return;
        }

        const { retryDelay = defaultRetryDelay } = optionsRef.current;
        retryTimer = setTimeout(open, retryDelay(attempt++));
      };
    };

    open();

    return () => {
      clearTimeout(retryTimer);
      source?.close();
    };
  }, [active, path, token]);
}
